// Start Date warnings — pure, view-only. Non-blocking by design (confirmed with the user): the
// check runs live off whatever issue data is already fetched (same-project links only, so every
// predecessor is always resolvable from the project's own issues list — see
// IssueService.addLink), so it naturally re-fires whenever either side of a dependency changes,
// not just at the moment a date/link is edited.

import type { IIssue, IssueDependencyType } from '@pulse/shared';

import { formatDdMmYyyy } from '../../../lib/date.util';

export interface DependencyWarning {
  linkId: string;
  predecessor: IIssue;
  dependencyType: IssueDependencyType;
  message: string;
}

function toDateOnly(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y!, m! - 1, d);
}

/** Only FS (Finish-to-Start) and SS (Start-to-Start) constrain a successor's *Start Date* — FF
 *  and SF constrain its Due/Completion Date instead (see `IssueDependencyType`'s own doc
 *  comment in packages/shared), so this never produces a warning for those two. Returns null
 *  when the predecessor doesn't have the date this dependency type needs yet, or when the
 *  candidate start date is already consistent with it. */
function checkOne(successorStartIso: string, dependencyType: IssueDependencyType, predecessor: IIssue): string | null {
  const successorStart = toDateOnly(successorStartIso);
  if (dependencyType === 'FS') {
    const predecessorEnd = predecessor.completionDate ?? predecessor.dueDate;
    if (!predecessorEnd) return null;
    if (successorStart < toDateOnly(predecessorEnd)) {
      return `Starts before ${predecessor.issueKey} is due to finish (${formatDdMmYyyy(predecessorEnd.slice(0, 10))}).`;
    }
  }
  if (dependencyType === 'SS') {
    if (!predecessor.startDate) return null;
    if (successorStart < toDateOnly(predecessor.startDate)) {
      return `Starts before ${predecessor.issueKey}'s own Start Date (${formatDdMmYyyy(predecessor.startDate)}).`;
    }
  }
  return null;
}

/** Checks a candidate Start Date against every 'Depends on' link in `links`, resolving each
 *  predecessor from `allIssues`. A predecessor not found in `allIssues` is skipped rather than
 *  treated as a violation — same-project links should always resolve from the project's own
 *  already-fetched issue list, so a miss here means the data just hasn't loaded yet, not that
 *  the dependency is broken. */
export function checkStartDateAgainstDependencies(
  successorStartIso: string,
  links: IIssue['links'],
  allIssues: IIssue[],
): DependencyWarning[] {
  const warnings: DependencyWarning[] = [];
  for (const link of links) {
    if (link.linkType !== 'Depends on' || !link.dependencyType) continue;
    const predecessor = allIssues.find((i) => i.id === link.linkedIssueId);
    if (!predecessor) continue;
    const message = checkOne(successorStartIso, link.dependencyType, predecessor);
    if (message) warnings.push({ linkId: link.id, predecessor, dependencyType: link.dependencyType, message });
  }
  return warnings;
}
